import { MEDIA_TABLET } from '@nike/ciclp-react-components/context/utils';
import { getHostName } from '@nike/ciclp-config';
import { CLICK_MOD_EVENTS } from '@nike/ciclp-utils';
import packageJson from '../../../package.json';
import {
  countryCodeSelector,
  languageCodeSelector,
  languageMappingsSelector,
} from '../../store/state/stateSelector';
import {
  classificationSelector,
  analyticsPageTypeSelector,
  pageNameSelector,
  globalNavPageNameSelector,
  pageDetailSelector,
  schemaSelector,
} from '../analyticsSelectors';
import { getAnalyticsEvent } from './analyticsEvent';
import {
  analyticsProperties,
  analyticsViewProperties,
  eventNames,
} from '../constants';
import { optimizationAnalyticsSelector } from '../../store/state/optimization/selectors';

const { APP_BACKEND_PLATFORM, PAGE_DIVISION, SITE, ENVIRONMENT, VERSION } =
  analyticsViewProperties;

/**
 * Get page load segment payload
 * @param {String} eventName analytics event name
 * @param {Object} state redux state object
 * @param {Object} eventData event details
 * @returns populated segment payload
 */

export const getSegmentPayload = ({ eventName, state, eventData = {} }) => {
  const classification = classificationSelector(eventName);
  const {
    clickActivity,
    video,
    product = {},
    modelId,
    modelInfo,
    modelVersion,
    selectedConcepts,
    selectedConceptsIds,
    ...content
  } = getAnalyticsEvent(eventName, state, eventData);
  const isPageEvent = eventName === eventNames.PAGE_VIEWED;
  const analytics = optimizationAnalyticsSelector(state);

  const abTest = {
    ...(isPageEvent && !!analytics && { analytics }),
  };

  const hasAbTest = Object.keys(abTest).length > 0;
  const hasModelData = modelInfo || modelId || modelVersion;
  const model = {
    modelId,
    modelInfo,
    modelVersion,
  };

  const contentData = {
    ...content,
    ...(eventName === eventNames.CLICK && {
      contentClickActivity: clickActivity,
    }),
  };

  const positionId = contentData?.positionId - 1;

  let { actionHashKeys } = eventData;
  // For multiple CTAs, check for comma-separated actionHashKeys
  // to split them into an array.
  if (actionHashKeys?.includes(',')) {
    actionHashKeys = actionHashKeys.split(',').map(s => s.trim());
  }

  // If actionHashKeys is not an array, convert it to an array.
  if (!Array.isArray(actionHashKeys)) {
    actionHashKeys = [actionHashKeys].filter(Boolean);
  }

  if (
    eventName === eventNames.IMPRESSION &&
    contentData?.assetId &&
    contentData?.cardKey &&
    contentData?.cardKey !== 'X' &&
    contentData?.threadKey &&
    contentData?.threadKey !== 'X'
  ) {
    const placementIdMatch = eventData.placementId.match(/^(.+)_([0-9]+)$/);
    const collectionId = placementIdMatch
      ? placementIdMatch[1]
      : eventData?.placementId;
    const index = placementIdMatch ? parseInt(placementIdMatch[2], 10) : 1;
    const itemCount = Number(eventData?.itemCount) || 1;
    CLICK_MOD_EVENTS.collectionItemViewedEvent({
      collectionItem: {
        collection: {
          collectionId,
          index: positionId,
          itemCount,
        },
        content: {
          underlyingContent: {
            case: 'cms',
            value: {
              actionKeys: actionHashKeys,
              assetId:
                contentData.assetId === 'X' ? undefined : contentData.assetId,
              cardKey: contentData.cardKey,
              threadKey: contentData.threadKey,
            },
          },
        },
        index: index - 1, // Respect original analytics index count, but keep it 0-based for new.
      },
    });
  }

  return {
    app: {
      backendPlatform: APP_BACKEND_PLATFORM,
      domain: getHostName(),
      version: VERSION,
    },
    classification,
    clickActivity,
    content: contentData,
    language: languageMappingsSelector(state)?.langRegion,
    locale: {
      country: countryCodeSelector(state),
      language: languageCodeSelector(state),
    },
    video,
    view: {
      experienceType: SITE,
      pageName: pageNameSelector(state),
      pageType: analyticsPageTypeSelector(state),
    },
    ...(hasAbTest && { abTest }),
    ...(hasModelData && { model: [model] }),
    ...(selectedConcepts && {
      [analyticsProperties.SELECTED_CONCEPTS]: selectedConcepts,
    }),
    ...(selectedConceptsIds && {
      [analyticsProperties.SELECTED_CONCEPTS_IDS]: selectedConceptsIds,
    }),
    ...product,
  };
};

/**
 * Get options for segment payload
 * @param {String} eventName analytics event name
 * @returns populated segment options
 */

export const getSegmentOptions = ({ eventName }) => {
  const schemaId = schemaSelector(eventName);

  return {
    $schema: schemaId,
  };
};

/**
 * Get page load mcJS (formerly known as NeoJS or DCM) payload
 * @param {String} eventName analytics event name
 * @param {Object} state redux state object
 * @returns populated McJS payload
 */

export const getMarketingPayload = ({ eventName, state }) => ({
  application: {
    country: countryCodeSelector(state),
    device: window.innerWidth < MEDIA_TABLET ? 'Mobile' : 'Desktop',
    language: (languageMappingsSelector(state)?.langRegion ?? '')
      .toLowerCase()
      .replace('-', '_'),
    view: analyticsPageTypeSelector(state),
  },
  consumer: {
    currencyType: '',
  },
  event: eventName,
  productList: [],
});

/**
 * Get dot com nav payload piece
 * @param {Object} state redux state object
 * @returns populated segment payload
 */

export const getDotComNavPayload = ({ state }) => ({
  appInfo: {
    division: PAGE_DIVISION,
    domain: SITE,
    environment: ENVIRONMENT,
    name: pageDetailSelector(state),
    platform: APP_BACKEND_PLATFORM,
    version: packageJson.version,
    viewChannel: analyticsPageTypeSelector(state),
    viewName: globalNavPageNameSelector(state),
  },
});
